import React, { useMemo, useCallback } from 'react'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useSites } from 'store'
import EmptyScreen from 'packages/ui/EmptyScreen'

import Card from './Card'
import Table from './Table'
import { useResetFilters } from '../../store'
import { useHasNotSiteSelected } from '../use-equipments'
import { HARDWARE_VIEW_MODES } from './use-hardware-view-mode'
import { getVisibleHardwareTypes, supportsCompleteDetails } from './hardware-types'
import CompleteDetailView from './CompleteDetailView'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <Stack
      sx={{
        display: value !== index ? 'none' : null,
        overflow: 'hidden',
        overflowY: 'auto',
        flexDirection: 'row',
        flex: '1 1 auto',
        height: 'inherit',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar ': {
          display: 'none'
        }
      }}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </Stack>
  )
}

const HardwaresView = ({ count, equipments, filterControls, loading, viewMode, buildSearch }) => {
  const { t } = useTranslation()
  const resetFilters = useResetFilters()
  const [{ focus }] = useSites()
  const navigate = useNavigate()
  const { hardwareType, uuid } = useParams()
  const hasNoSiteSelected = useHasNotSiteSelected()

  const visibleHardwareTypes = useMemo(() => getVisibleHardwareTypes(count), [count])
  const activeHardwareType = useMemo(() => {
    if (visibleHardwareTypes.some(({ value }) => value === hardwareType)) return hardwareType
    return visibleHardwareTypes[0]?.value || hardwareType || 'access_points'
  }, [hardwareType, visibleHardwareTypes])
  const detailsEnabled = supportsCompleteDetails(activeHardwareType)
  const effectiveViewMode = detailsEnabled
    ? viewMode
    : HARDWARE_VIEW_MODES.SIMPLE
  const activeDevices = useMemo(() => equipments[activeHardwareType] || [], [equipments, activeHardwareType])

  const hasNoEquipment = visibleHardwareTypes.length === 0

  const changeTab = (newValue) => {
    if (focus) {
      navigate(`/monitoring/infras/${focus.sf_id}/${newValue}${buildSearch({ view: viewMode })}`)
    } else {
      navigate(`/monitoring/infras/hardwares/${newValue}${buildSearch({ view: viewMode })}`)
    }
  }

  const renderCard = useCallback(() => {
    if (!uuid) return null

    const equipment = equipments[activeHardwareType]?.find(item => item.uuid === uuid)
    if (!equipment) return null

    return <Card equipment={equipment} />
  }, [uuid, equipments, activeHardwareType])

  const selectedEquipment = useMemo(() => {
    if (!uuid) return null
    return activeDevices.find(item => item.uuid === uuid) || null
  }, [activeDevices, uuid])

  if (hasNoSiteSelected) {
    return (
      <EmptyScreen icon="buildings" variant="panel">
        {t('supervision.tables.empty_no_site_selected')}
      </EmptyScreen>
    )
  } else if (!loading && hasNoEquipment) {
    return (
      <EmptyScreen icon="buildings" variant="panel">
        {t('supervision.tables.empty')}
      </EmptyScreen>
    )
  }

  if (effectiveViewMode === HARDWARE_VIEW_MODES.COMPLETE && selectedEquipment) {
    return (
      <CompleteDetailView
        equipment={selectedEquipment}
        equipments={activeDevices}
        hardwareType={activeHardwareType}
        basePath={`/monitoring/infras/hardwares/${activeHardwareType}`}
        buildSearch={buildSearch}
      />
    )
  }

  const table = (
    <Box sx={{ height: '100%', minHeight: 260, flex: '1 1 auto', minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Table
        equipments={activeDevices}
        hardwareType={activeHardwareType}
        loading={loading}
        viewMode={effectiveViewMode}
        basePath={`/monitoring/infras/hardwares/${activeHardwareType}`}
        buildSearch={buildSearch}
        detailsEnabled={detailsEnabled}
      />
    </Box>
  )

  const renderTablePanel = (withDetails = false) => (
    <Stack
      gap="10px"
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        border: '1px solid #EFEFEF',
        borderRadius: '24px',
        backgroundColor: '#fff',
        p: 2.5
      }}
    >
      {filterControls}
      <Stack
        direction="row"
        gap={withDetails ? 0 : '10px'}
        sx={{ flex: '1 1 auto', minWidth: 0, width: '100%', maxWidth: '100%', minHeight: 0, overflow: 'hidden' }}
      >
        {table}
        {withDetails && detailsEnabled && renderCard()}
      </Stack>
    </Stack>
  )

  return (
    <Stack gap="10px" sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: '100%', minHeight: 0, overflow: 'hidden', pt: 0 }}>
      <Stack direction="row" gap="10px" flexWrap="wrap" sx={{ flexShrink: 0 }}>
        {loading ? visibleHardwareTypes.map(type => (
          <Box
            key={type.value}
            sx={{
              width: 116,
              height: 72,
              border: '1px solid #EFEFEF',
              borderRadius: '16px',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Stack alignItems="center" gap={0.5}>
              <Skeleton variant="circular" width={18} height={18} sx={{ backgroundColor: '#F1F1F1' }} />
              <Skeleton variant="rounded" width={54} height={8} sx={{ backgroundColor: '#F3F3F3' }} />
              <Skeleton variant="rounded" width={34} height={10} sx={{ backgroundColor: '#F3F3F3' }} />
            </Stack>
          </Box>
        )) : visibleHardwareTypes.map(type => {
          const total = count[type.countKey] || 0
          const ok = count[type.okKey] ?? total
          const isActive = activeHardwareType === type.value
          return (
            <Box
              key={type.value}
              onClick={() => {
                resetFilters()
                changeTab(type.value)
              }}
              sx={{
                minWidth: 116,
                height: 72,
                border: `${isActive ? 2 : 1}px solid ${isActive ? '#2570EB' : '#EFEFEF'}`,
                borderRadius: '16px',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                  borderColor: isActive ? '#2570EB' : '#EFEFEF',
                  backgroundColor: '#FBFBFB'
                }
              }}
            >
              <Stack alignItems="center" justifyContent="center" gap={0.35}>
                <Box component="i" className={type.icon} sx={{ fontSize: 18, color: isActive ? '#2570EB' : '#464646' }} />
                <Typography sx={{ fontSize: 10, color: '#656565', fontWeight: 400, whiteSpace: 'nowrap' }}>
                  {t(type.labelKey)}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 400 }}>
                  <Box component="span" sx={{ color: '#656565' }}>{ok}/</Box>
                  <Box component="span" sx={{ color: '#989898' }}>{total}</Box>
                </Typography>
              </Stack>
            </Box>
          )
        })}
      </Stack>

      {visibleHardwareTypes.map(type => (
        <TabPanel key={type.value} value={activeHardwareType} index={type.value}>
          {effectiveViewMode === HARDWARE_VIEW_MODES.COMPLETE || !detailsEnabled ? (
            renderTablePanel()
          ) : (
            renderTablePanel(true)
          )}
        </TabPanel>
      ))}
    </Stack>
  )
}

export default HardwaresView
